import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  User, 
  Shield, 
  Lock, 
  Mail, 
  Calendar,
  Phone,
  MapPin,
  CheckCircle,
  Settings,
  XCircle,
  Heart,
  Activity
} from 'lucide-react';
import { useTranslation } from '../../i18n/i18n';
import api from '../../api/axios';
import { toast } from 'react-toastify';

const SettingsPage = ({ lang }) => {
  const navigate = useNavigate();
  const { t } = useTranslation(lang);
  const profile_url = process.env.REACT_APP_API_PROFILE_URL;

  const [userData, setUserData] = useState({
    id: '',
    full_name: '',
    email: '',
    phone: '',
    status: '',
    address: '',
    created_at: '',
    sessionLog: []
  });

  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchProfileData();
  }, []);

  const fetchProfileData = async () => {
    try {
      const response = await api.get(profile_url);

      const data = await response.data;
      
      if (data.result) {
        setUserData({
          id: data.result.id || '',
          full_name: data.result.full_name || '',
          email: data.result.email || '',
          phone: data.result.phone || '',
          status: data.result.status || '',
          address: data.result.address || '',
          created_at: data.result.created_at || '',
          sessionLog: data.result.sessionLog || []
        });
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error(t('pleaseReconnect'));
    } finally {
      setLoading(false);
    }
  };

  const styles = {
    container: {
      padding: '24px',
      background: '#f9fafb',
      minHeight: '100vh'
    },
    header: {
      marginBottom: '32px'
    },
    title: {
      fontSize: '28px',
      fontWeight: 'bold',
      color: '#1f2937',
      margin: 0,
      display: 'flex',
      alignItems: 'center',
      gap: '12px'
    },
    section: {
      background: 'white',
      borderRadius: '12px',
      padding: '24px',
      marginBottom: '24px',
      boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
    },
    sectionTitle: {
      fontSize: '20px',
      fontWeight: '600',
      color: '#1f2937',
      marginBottom: '20px',
      display: 'flex',
      alignItems: 'center',
      gap: '12px'
    },
    grid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
      gap: '24px'
    },
    infoCard: {
      background: '#f8fafc',
      padding: '20px',
      borderRadius: '8px',
      border: '1px solid #e5e7eb'
    },
    infoItem: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '12px 0',
      borderBottom: '1px solid #f3f4f6'
    },
    infoLabel: {
      fontWeight: '600',
      color: '#374151',
      fontSize: '14px',
      display: 'flex',
      alignItems: 'center',
      gap: '8px'
    },
    infoValue: {
      color: '#6b7280',
      fontSize: '14px'
    },
    verificationBadge: {
      display: 'flex',
      alignItems: 'center',
      gap: '6px',
      padding: '4px 8px',
      borderRadius: '12px',
      fontSize: '12px',
      fontWeight: '600'
    },
    verified: {
      background: '#d1fae5',
      color: '#065f46'
    },
    notVerified: {
      background: '#fef3c7',
      color: '#92400e'
    },
    settingsGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))',
      gap: '20px'
    },
    settingCard: {
      background: 'white',
      padding: '24px',
      borderRadius: '12px',
      border: '1px solid #e5e7eb',
      backgroundColor: '#f8fafc',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      display: 'flex',
      alignItems: 'center',
      gap: '16px'
    },
    settingIcon: {
      width: '48px',
      height: '48px',
      background: 'linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)',
      borderRadius: '12px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: 'white',
      flexShrink: 0
    },
    settingContent: {
      flex: 1
    },
    settingTitle: {
      fontSize: '18px',
      fontWeight: '600',
      color: '#1f2937',
      marginBottom: '4px'
    },
    settingDescription: {
      fontSize: '14px',
      color: '#6b7280',
      lineHeight: '1.4'
    }
  };

  const handleNavigation = (path) => {
    navigate(path);
  };

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.header}>
          <h1 style={styles.title}>
            <Settings size={32} color="#7c3aed" />
            {t('settings')}
          </h1>
        </div>
        <div style={{ textAlign: 'center', padding: '40px' }}>
          Chargement...
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <h1 style={styles.title}>
          <Settings size={32} color="#7c3aed" />
          {t('settings')}
        </h1>
      </div>

      {/* Informations Personnelles */}
      <div style={styles.section}>
        <h2 style={styles.sectionTitle}>
          <User size={24} />
          {t('personalInformation')}
        </h2>
        
        <div style={styles.grid}>
          <div style={styles.infoCard}>
            <div style={styles.infoItem}>
              <span style={styles.infoLabel}>
                <User size={16} />
                ID
              </span>
              <span style={styles.infoValue}>#{userData.id}</span>
            </div>
            <div style={styles.infoItem}>
              <span style={styles.infoLabel}>
                <User size={16} />
                {t('name')}
              </span>
              <span style={styles.infoValue}>{userData.full_name}</span>
            </div>
            <div style={styles.infoItem}>
              <span style={styles.infoLabel}>
                <Mail size={16} />
                {t('email')}
              </span>
              <span style={styles.infoValue}>{userData.email}</span>
            </div>
          </div>

          <div style={styles.infoCard}>
            <div style={styles.infoItem}>
              <span style={styles.infoLabel}>
                <Phone size={16} />
                {t('phone')}
              </span>
              <span style={styles.infoValue}>{userData.phone || t('notProvided')}</span>
            </div>
            <div style={styles.infoItem}>
              <span style={styles.infoLabel}>
                <MapPin size={16} />
                {t('address')}
              </span>
              <span style={styles.infoValue}>{userData.address || t('notProvided')}</span>
            </div>
            <div style={styles.infoItem}>
              <span style={styles.infoLabel}>
                <Heart size={16} />
                {t('status')}
                </span>
              <span style={{
                ...styles.verificationBadge,
                ...(userData.status === 'enabled' ? styles.verified : styles.notVerified)
              }}>
                {userData.status === 'enabled' ? <CheckCircle size={14} /> : <XCircle size={14} />}
                {userData.status === 'enabled' ? t('enabled') : t('disabled')}
              </span>
            </div>
          </div>

          <div style={styles.infoCard}>
            <div style={styles.infoItem}>
              <span style={styles.infoLabel}>
                <Calendar size={16} />
                {t('createdAt')}
              </span>
              <span style={styles.infoValue}>
                {userData.created_at ? userData.created_at : t('unknown')}
              </span>
            </div>

                        <div style={styles.infoItem}>
              <span style={styles.infoLabel}>
                <Activity size={16} />
                {t('connexions')}
              </span>
              <span style={styles.infoValue}>{userData.sessionLog.length || t('notProvided')}</span>
            </div>

          </div>
        </div>
      </div>

      {/* Paramètres */}
      <div style={styles.section}>
        <h2 style={styles.sectionTitle}>
          <Shield size={24} />
          {t('accountSettings')}
        </h2>

        <div style={styles.settingsGrid}>
          {/* Connexions et Sécurité */}
          <div 
            style={styles.settingCard}
            onClick={() => handleNavigation('/settings/security')}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-4px)';
              e.currentTarget.style.boxShadow = '0 8px 25px rgba(0,0,0,0.15)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = 'none';
            }}
          >
            <div style={styles.settingIcon}>
              <Lock size={24} />
            </div>
            <div style={styles.settingContent}>
              <div style={styles.settingTitle}>{t('securitySettings')}</div>
              <div style={styles.settingDescription}>
                {t('securitySettingsDescription')}
              </div>
            </div>
          </div>

          {/* Informations Personnelles */}
          <div 
            style={styles.settingCard}
            onClick={() => handleNavigation('/settings/profile')}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-4px)';
              e.currentTarget.style.boxShadow = '0 8px 25px rgba(0,0,0,0.15)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = 'none';
            }}
          >
            <div style={styles.settingIcon}>
              <User size={24} />
            </div>
            <div style={styles.settingContent}>
              <div style={styles.settingTitle}>{t('profileSettings')}</div>
              <div style={styles.settingDescription}>
                {t('profileSettingsDescription')}
              </div>
            </div>
          </div>

          {/* Sessions Actives */}
          <div 
            style={styles.settingCard}
            onClick={() => handleNavigation('/settings/sessions')}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-4px)';
              e.currentTarget.style.boxShadow = '0 8px 25px rgba(0,0,0,0.15)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = 'none';
            }}
          >
            <div style={styles.settingIcon}>
              <Shield size={24} />
            </div>
            <div style={styles.settingContent}>
              <div style={styles.settingTitle}>{t('sessions')}</div>
              <div style={styles.settingDescription}>
                {t('activeSessionsDescription')}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SettingsPage;